export type ArrowNavigationDirection = "previous" | "next"

const INPUT_TYPES_BLOCKLIST = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
])

const FOCUSABLE_SELECTOR = [
  "input:not([type='hidden']):not([disabled]):not([aria-disabled='true'])",
  "textarea:not([disabled]):not([aria-disabled='true'])",
  "select:not([disabled]):not([aria-disabled='true'])",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1']):not([disabled]):not([aria-disabled='true'])",
].join(",")

const isVisible = (element: HTMLElement) => {
  return element.offsetParent !== null || element.getClientRects().length > 0
}

const getFocusableFields = (container: ParentNode): HTMLElement[] => {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => isVisible(element)
  )
}

const getNavigationContainer = (element: HTMLElement): ParentNode => {
  const form = element.closest("form")
  return form ?? element.ownerDocument
}

const isArrowNavigationAllowed = (element: HTMLElement) => {
  if (element instanceof HTMLInputElement) {
    return !INPUT_TYPES_BLOCKLIST.has(element.type)
  }

  if (element instanceof HTMLSelectElement) {
    return true
  }

  return element instanceof HTMLTextAreaElement || element.isContentEditable
}

const isCaretAtBoundary = (
  element: HTMLInputElement | HTMLTextAreaElement,
  direction: ArrowNavigationDirection
) => {
  if (element instanceof HTMLTextAreaElement) {
    if (direction === "previous") {
      return element.selectionStart === 0
    }

    return element.selectionEnd === element.value.length
  }

  return true
}

type ArrowNavigationEvent = {
  key: string
  target: EventTarget | null
  preventDefault: () => void
}

const resolveNavigationTarget = (event: ArrowNavigationEvent): HTMLElement | null => {
  if (event.target instanceof HTMLElement) {
    return event.target
  }

  if (document.activeElement instanceof HTMLElement) {
    return document.activeElement
  }

  return null
}

export const handleArrowFieldNavigation = (event: ArrowNavigationEvent) => {
  const { key } = event
  const normalizedKey =
    key === "ArrowUp" || key === "Up"
      ? "ArrowUp"
      : key === "ArrowDown" || key === "Down"
        ? "ArrowDown"
        : null

  if (!normalizedKey) {
    return
  }

  const target = resolveNavigationTarget(event)
  if (!target) {
    return
  }

  if (!isArrowNavigationAllowed(target)) {
    return
  }

  if (
    (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) &&
    !isCaretAtBoundary(target, normalizedKey === "ArrowUp" ? "previous" : "next")
  ) {
    return
  }

  const container = getNavigationContainer(target)
  const focusable = getFocusableFields(container)
  const currentIndex = focusable.indexOf(target)

  if (currentIndex === -1) {
    return
  }

  const nextIndex =
    normalizedKey === "ArrowUp" ? currentIndex - 1 : currentIndex + 1
  const nextElement = focusable[nextIndex]

  if (!nextElement) {
    return
  }

  event.preventDefault()
  nextElement.focus()
}
